import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import DarkPageHero from "../components/DarkPageHero";

export default function HowItWorksPage() {
  const steps = [
    {
      number: "1",
      title: "Enter Your Financial Details",
      description: "Tell us about your employment type, income, existing debts, and the loan amount you're looking for. This helps us match you with the right banks.",
      details: [
        "Employment type (government, private, self-employed)",
        "Monthly income and other income sources",
        "Existing debts and monthly payments",
        "Desired loan amount and purpose"
      ]
    },
    {
      number: "2",
      title: "See Estimated Loan Matches",
      description: "Our system evaluates your profile against the lending criteria of 5 major Bahamian banks and shows you which ones you're likely to qualify with.",
      details: [
        "Instant evaluation against bank criteria",
        "Estimated loan amounts you may qualify for",
        "Projected monthly payments",
        "Interest rate comparisons"
      ]
    },
    {
      number: "3",
      title: "Connect with the Right Bank",
      description: "Get your detailed pre-qualification report and use it to approach banks with confidence. Know your options before you apply.",
      details: [
        "Downloadable PDF report",
        "Side-by-side bank comparison",
        "Clear next steps for each bank",
        "Save results to your account"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <DarkPageHero
        overline="HOW IT WORKS"
        title={<>Three steps. <span className="text-[#52BFB8]">Zero credit impact.</span></>}
        subtitle="Pre-qualify across 5+ Caribbean banks in under five minutes."
      />

      {/* Steps */}
      <section className="py-16">
        <div className="container-main">
          <div className="max-w-4xl mx-auto space-y-12">
            {steps.map((step, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-8 md:p-10">
                  <div className="flex items-start gap-6">
                    <div 
                      className="w-16 h-16 rounded-2xl flex items-center justify-center text-white text-2xl font-bold flex-shrink-0"
                      style={{ 
                        background: index === 2 
                          ? 'linear-gradient(135deg, #FDD552 0%, #F5C842 100%)' 
                          : 'linear-gradient(135deg, #52BFB8 0%, #3DA8A1 100%)'
                      }}
                    >
                      {step.number}
                    </div>
                    <div className="flex-1">
                      <h2 className="text-2xl font-bold mb-3" style={{ color: '#2D3748' }}>
                        {step.title}
                      </h2>
                      <p className="text-gray-600 mb-6">
                        {step.description}
                      </p>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {step.details.map((detail, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{ color: '#52BFB8' }} />
                            <span className="text-sm text-gray-600">{detail}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <Link to="/qualify">
              <Button className="btn-primary text-lg px-10 py-4 h-auto group">
                Start Your Pre-Qualification
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <p className="text-sm text-gray-500 mt-4">
              Takes less than 5 minutes. No credit impact.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
